import { Alert, Button, Col, DatePicker, Form, Modal, Row, Typography } from "antd";
import useDirectDebitApi from "views/Payments/hooks/useDirectDebitApi";
import { DATE_FORMAT, DATE_FORMAT_API } from "config/settings";
import InvoicesToBeDrawnTable from "./InvoicesToBeDrawnTable";
import useNotification from "utils/hooks/useNotification";
import usePaymentsApi from "../../hooks/usePaymentsApi";
import useCoreStore from "views/hooks/useCoreStore";
import { FaArchive, FaSave } from "react-icons/fa";
import { ApolloQueryResult } from "@apollo/client";
import { getFormError } from "utils/getFormError";
import MissingInfoTable from "./MissingInfoTable";
import TooltipButton from "views/Documents/components/TooltipButton";
import { MdEdit } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { locale } from "utils/locale";
import dayjs from "dayjs";
import React from "react";

interface CreateUpdateDebitsPropsTypes {
  id?: ID;
  refetch: (
    variables?: Partial<ListDirectDebitsVars> | undefined,
  ) => Promise<ApolloQueryResult<ListDirectDebitsResponse>>;
}

export default function CreateUpdateDebits(
  props: CreateUpdateDebitsPropsTypes,
): React.ReactElement {
  const { id, refetch } = props;
  const [form] = Form.useForm();
  const urlParams: RouteParams = useParams();
  const { t } = useTranslation();

  const { notificationError, notificationSuccessfullySaved } = useNotification();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const companySettings = useCoreStore(state => state.companySettings);
  const defaultBankInfo = companySettings?.defaultBankAccount;

  const requiredProps = ["bic", "iban", "sepaCredentialIdentifier"];
  const canDoDebit = requiredProps?.every(prop =>
    Object.keys(defaultBankInfo || {}).includes(prop),
  );

  const ibanIsMissing = defaultBankInfo?.iban;
  const bicIsMissing = defaultBankInfo?.bic;
  const sepaCredentialIsMissing = defaultBankInfo?.sepaCredentialIdentifier;

  const [deadlineDate, setDeadlineDate] = useState();
  const [selectedRows, setSelectedRows] = useState<any>({
    selectedRowKeys: [],
    selectedItems: [],
    loading: false,
  });

  const { createUpdateDirectDebit, setListInvoicesData, ddSaleDocsCount } = useDirectDebitApi(id);
  const { payment: data } = usePaymentsApi(id);

  /*
   * InvoicesToBeDrawnTable issues this query itself, with the page size the
   * table actually renders. Firing an unpaginated copy here raced it and the
   * limitless response won, capping the list at the server default.
   */

  useEffect(() => {
    if (data && id) {
      const fieldsData: any = [];
      for (const key in data) {
        let value = (data as Record<string, any>)[key];
        if (key === "date" && (data as Record<string, any>)[key]) {
          value = dayjs((data as Record<string, any>)[key]);
        } else if (key === "group" && (data as Record<string, any>)[key]) {
          value = (data as Record<string, any>)[key]?.id;
        }
        fieldsData.push({
          name: key,
          value: value,
        });
      }
      form.setFields(fieldsData);
    }
  }, [data]);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleOk = () => {
    form.submit();
  };

  const onFinish = async (values: Record<string, any>) => {
    const updateId = id ? { id } : {};
    let errors: any = [];

    const variables = {
      input: {
        amount: values.amount,
        reference: values.reference,
        saleDocuments: selectedRows?.selectedItems?.map((item: Record<string, any>) => item.id),
        date: dayjs(values.date).format(DATE_FORMAT_API),
        ...updateId,
      },
    };

    const response = await createUpdateDirectDebit({ variables });

    errors = urlParams.clientId
      ? response?.data?.updateDirectDebit?.errors
      : response?.data?.createDirectDebit?.errors;

    if (errors && errors.length > 0) {
      const fieldsData = getFormError(errors, values);
      notificationError(errors);
      form.setFields(fieldsData);
    } else {
      refetch();
      notificationSuccessfullySaved("Payment");
      setIsModalVisible(false);
      form.resetFields();
      setSelectedRows({
        selectedRowKeys: [],
        selectedItems: [],
        loading: false,
      });
      setListInvoicesData([]);
      setDeadlineDate(undefined);
    }
  };

  const onValuesChange = (changedValues: Record<string, any>) => {
    setDeadlineDate(changedValues?.date);
  };

  return (
    <>
      {id ? (
        <TooltipButton placement="top" handleOnClick={showModal} icon={<MdEdit />} />
      ) : (
        <Row justify="end" className="margin-top">
          <Button type="primary" onClick={showModal}>
            {t("Add")}
          </Button>
        </Row>
      )}

      <Modal
        title={t("Add payment")}
        open={isModalVisible}
        onOk={handleOk}
        okText={t("Save")}
        okButtonProps={{
          icon: (
            <span className="anticon">
              <FaSave />
            </span>
          ),
          disabled: !canDoDebit,
        }}
        onCancel={handleCancel}
        width={"92%"}
        className="antd-modal debits-modal"
        wrapClassName="debits-modal-wrap"
      >
        <Form
          name="createDebits"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          onValuesChange={onValuesChange}
          autoComplete="off"
          className="debits-modal__form"
        >
          <Row gutter={[8, 8]}>
            {!ibanIsMissing && (
              <Col span={24}>
                <Alert message={t("IBAN is missing")} type="error" showIcon />
              </Col>
            )}
            {!bicIsMissing && (
              <Col span={24}>
                <Alert message={t("BIC is missing")} type="error" showIcon />
              </Col>
            )}
            {!sepaCredentialIsMissing && (
              <Col span={24}>
                <Alert message={t("SEPA identifier is missing")} type="error" showIcon />
              </Col>
            )}
            <Col span={24}>
              <div className="debits-modal__controls">
                <Form.Item
                  name="date"
                  label={t("Date")}
                  rules={[{ required: true, message: t("This field is required") }]}
                >
                  <DatePicker
                    locale={locale as any}
                    format={[DATE_FORMAT, "DDMMYYYY", "DD-MM-YYYY"]}
                  />
                </Form.Item>
                {deadlineDate && Number(ddSaleDocsCount) > 0 && (
                  <span className="debits-modal__tally">
                    <strong>{selectedRows?.selectedRowKeys?.length || 0}</strong>
                    {" / "}
                    <strong>{ddSaleDocsCount}</strong> {t("Invoices to be drawn").toLowerCase()}
                  </span>
                )}
              </div>
            </Col>
            <Col span={24} className="debits-modal__tables-col">
              {deadlineDate && (
                <Typography.Title level={5} className="debits-modal__section-title">
                  <span className="anticon">
                    <FaArchive />
                  </span>
                  <span>{t("Invoices to be drawn")}</span>
                  {Number(ddSaleDocsCount) > 0 && (
                    <span className="debits-modal__count">{ddSaleDocsCount}</span>
                  )}
                </Typography.Title>
              )}
              <InvoicesToBeDrawnTable
                setSelectedRows={setSelectedRows}
                selectedRows={selectedRows}
                deadlineDate={deadlineDate}
              />
              <MissingInfoTable deadlineDate={deadlineDate} />
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
